/**
 * Network Types for MangoGrid Studio Pro
 * Defines the core data structures for nodes and connections
 */

// ============================================================
// NODE TYPES
// ============================================================

/** The available types of network nodes */
export type NodeType = 'bus' | 'generator' | 'load' | 'storage';

/** Properties specific to bus nodes */
export interface BusProperties {
  v_nom: number;
  v_mag_pu?: number;
  v_ang?: number;
}

/** Properties specific to generator nodes */
export interface GeneratorProperties {
  p_nom: number;
  fuel_type?: string;
  marginal_cost?: number;
  efficiency?: number;
}

/** Properties specific to load nodes */
export interface LoadProperties {
  p_set: number;
  q_set?: number;
}

/** Properties specific to storage nodes */
export interface StorageProperties {
  p_nom: number;
  max_hours: number;
}

/** Union type for all node properties */
export type NodeProperties = BusProperties | GeneratorProperties | LoadProperties | StorageProperties;

/** Common node fields */
export interface BaseNode {
  /** Unique identifier */
  id: string;
  /** Type of the node */
  type: NodeType;
  /** X coordinate on canvas */
  x: number;
  /** Y coordinate on canvas */
  y: number;
  /** Display name */
  name: string;
}

/** Bus node */
export interface BusNode extends BaseNode {
  type: 'bus';
  properties: BusProperties;
}

/** Generator node */
export interface GeneratorNode extends BaseNode {
  type: 'generator';
  properties: GeneratorProperties;
}

/** Load node */
export interface LoadNode extends BaseNode {
  type: 'load';
  properties: LoadProperties;
}

/** Storage node */
export interface StorageNode extends BaseNode {
  type: 'storage';
  properties: StorageProperties;
}

/** A network node (bus, generator, load, or storage) */
export type Node = BusNode | GeneratorNode | LoadNode | StorageNode;

/** Type guard for bus nodes */
export function isBus(node: Node): node is BusNode {
  return node.type === 'bus';
}

/** Type guard for generator nodes */
export function isGenerator(node: Node): node is GeneratorNode {
  return node.type === 'generator';
}

/** Type guard for load nodes */
export function isLoad(node: Node): node is LoadNode {
  return node.type === 'load';
}

/** Type guard for storage nodes */
export function isStorage(node: Node): node is StorageNode {
  return node.type === 'storage';
}

// ============================================================
// CONNECTION TYPES
// ============================================================

/** The available types of connections */
export type ConnectionType = 'line' | 'transformer' | 'attachment';

/** Properties for transmission lines */
export interface LineProperties {
  /** Resistance (p.u.) */
  r: number;
  /** Reactance (p.u.) */
  x: number;
  /** Rated apparent power (MVA) */
  s_nom: number;
  /** Susceptance (optional) */
  b?: number;
}

/** Properties for transformers */
export interface TransformerProperties {
  /** Rated apparent power (MVA) */
  s_nom: number;
  /** Tap ratio */
  tap_ratio?: number;
  /** Phase shift (degrees) */
  phase_shift?: number;
}

/** Properties for attachment connections (non-line connectors) */
export interface AttachmentProperties {
  // Intentionally empty; attachments are topological links only.
}

/** Union type for all connection properties */
export type ConnectionProperties = LineProperties | TransformerProperties | AttachmentProperties;

/** A connection between two nodes */
export interface Connection {
  /** Unique identifier */
  id: string;
  /** Type of the connection */
  type: ConnectionType;
  /** ID of the source node */
  from: string;
  /** ID of the target node */
  to: string;
  /** Type-specific properties */
  properties: ConnectionProperties;
}

/** Type guard for line connections */
export function isLine(connection: Connection): connection is Connection & { properties: LineProperties } {
  return connection.type === 'line';
}

/** Type guard for attachment connections */
export function isAttachment(connection: Connection): connection is Connection & { properties: AttachmentProperties } {
  return connection.type === 'attachment';
}

// ============================================================
// NETWORK STATE
// ============================================================

/** Complete network state with nodes and connections */
export interface NetworkState {
  nodes: Node[];
  connections: Connection[];
}

// ============================================================
// NODE TYPE CONFIGURATION
// ============================================================

/** Configuration for a node type (display properties) */
export interface NodeTypeConfig {
  /** Display label */
  label: string;
  /** Color for rendering */
  color: string;
  /** Icon/emoji for display */
  icon: string;
}

/** Map of node types to their configurations */
export type NodeTypesConfig = Record<NodeType, NodeTypeConfig>;

